import { PLANO_EMPRESA_ENUM } from 'util/Enum';

/**
 * Catálogo de acessos: uma entrada por código, sem ordem relevante.
 *
 * Formato da regra (combináveis):
 *   flag: 'coluna'            libera pela coluna booleana do nível (padrão antigo)
 *   flags: ['a', 'b']         libera se QUALQUER uma das colunas estiver ligada
 *   todasFlags: ['a', 'b']    libera se TODAS as colunas estiverem ligadas
 *   granular: true            libera se authUserInfo.permissoes contiver o código (padrão novo)
 *   qualquerFlag: [...]       agrupador de menu: qualquer coluna da lista ligada
 *   qualquerGranular: [...]   agrupador de menu: qualquer permissão da lista presente
 *   qualquerAcesso: [...]     agrupador de menu: qualquer CÓDIGO do catálogo liberado
 *                             (resolve o filho pela regra dele, plano incluso)
 *   planos: [...]             pré-requisito de plano da empresa; fora dele, nega
 *   sempre: true              libera para todo perfil não-negociador
 *   negociador: true | fn     o que o NEGOCIADOR enxerga; ausente = negado
 *
 * Código do padrão novo (com ponto) sem nenhuma fonte declarada é tratado como
 * `granular` automaticamente, então `'modulo.recurso.acao': {}` já funciona.
 */

const PLANOS_PRO = [PLANO_EMPRESA_ENUM.PRO, PLANO_EMPRESA_ENUM.BUSINESS];
const PLANOS_STARTUP_PRO = [PLANO_EMPRESA_ENUM.STARTUP, ...PLANOS_PRO];
const PLANOS_BUSINESS = [PLANO_EMPRESA_ENUM.BUSINESS];
const PLANOS_STARTUP = [PLANO_EMPRESA_ENUM.STARTUP];

const granular = (...codigos) =>
  codigos.reduce(
    (acc, codigo) => ({ ...acc, [codigo]: { granular: true } }),
    {}
  );

export const CATALOGO = {
  //* ***************************************************** COBRANCA */
  cob_boletos: { flag: 'bol_v', negociador: true },
  cob_pix: { flag: 'pix_v', negociador: true },
  cob_historicos: { flag: 'rel_hist_oco', negociador: true },
  cob_acordos_lib: { flag: 'acordos_lib', negociador: true },
  cob_recident: { flag: 'receb_bloq', negociador: true },
  cob_modelo_oco: { flag: 'cob_modelo_oco', negociador: true },
  cob_propostas: { flag: 'cob_propostas', negociador: true },
  cob_agenda_ret: { flag: 'agenda', negociador: true },
  cob_acordos_prog: { flag: 'acordos', negociador: true },
  cob_gravacoes_chamadas: { flag: 'gravacao_chamada', negociador: true },
  cob_comissao: { flag: 'comissao', negociador: true },
  cob_acionar: { flag: 'dev_aciona', negociador: true },
  instrucao_online: { flag: 'dev_aciona', negociador: true },
  'mensagens.colab': { sempre: true, negociador: true },
  'cob.processo.visualizar': { granular: true, negociador: true },

  cob_exportar_xls: {
    sempre: true,
    negociador: (info) => !info.bloquear_exportar_negociador,
  },
  cob_excluir_digitalizacao: {
    sempre: true,
    negociador: (info) => !info.bloquear_excluir_digitalizacao_negociador,
  },

  //* ***************************************************** CONFIG DO NEGOCIADOR */
  conf_neg_ativa_credor: {
    negociador: (info) => info.conf_neg_ativa_credor,
  },
  conf_neg_inativa_obs_credor: {
    negociador: (info) => info.conf_neg_inativa_obs_credor,
  },
  acesso_taxa_contrato: {
    flag: 'perm_taxa_contrato',
    negociador: (info) => info.conf_neg_ativa_taxa,
  },

  //* ***************************************************** CADASTRO */
  modulo_cadastros: {
    qualquerFlag: [
      'cre_v',
      'grp_cre',
      'dev_v',
      'dev_tit',
      'colab_v',
      'grp_colab',
      'bancos',
      'contas_banc',
      'centro_custo',
      'plano_conta',
      'fases',
      'segmentos',
      'modal_contrato',
      'comissao_escalonada',
    ],
  },

  cad_colaborador_v: { flag: 'colab_v' },
  cad_colaborador_c: { flag: 'colab_c' },
  cad_colaborador_a: { flag: 'colab_a' },
  cad_colaborador_e: { flag: 'colab_e' },
  cad_colaborador_grupo: { flag: 'grp_colab' },

  cad_credor_v: { flag: 'cre_v' },
  cad_credor_c: { flag: 'cre_c' },
  cad_credor_a: { flag: 'cre_a' },
  cad_credor_e: { flag: 'cre_e' },
  cad_credor_grupo: { flag: 'grp_cre' },
  cad_credor_v_a: { flags: ['cre_v', 'cre_a'] },

  cad_devedor_v: {
    flag: 'dev_v',
    negociador: (info) => !info.bloq_lista_devedor,
  },
  cad_devedor_c: { flag: 'dev_c' },
  cad_devedor_a: { flag: 'dev_a' },
  cad_devedor_e: { flag: 'dev_e' },

  cad_titulo_v: { flag: 'dev_tit' },
  cad_titulo_c: { flag: 'dev_tit_c' },
  cad_titulo_a: { flag: 'dev_tit_a' },
  cad_titulo_e: { flag: 'dev_tit_e' },

  cad_fornecedor_v: { flag: 'forne_v' },
  cad_fornecedor_c: { flag: 'forne_c' },
  cad_fornecedor_a: { flag: 'forne_a' },
  cad_fornecedor_e: { flag: 'forne_e' },

  cad_multi_empresa: { flag: 'habilitar_multiempresa' },

  //* ***************************************************** TABELAS */
  tab_bancos: { flag: 'bancos' },
  tab_contas: { flag: 'contas_banc' },
  tab_custo: { flag: 'centro_custo' },
  tab_plano_conta: { flag: 'plano_conta' },
  tab_forma_pagto: { flag: 'forma_pagto' },
  tab_empresas: { flag: 'empresas' },
  tab_classifica_acordo: { flag: 'classificacao_acordo' },
  tab_categoria_instrucao: { flag: 'categoria_instrucao' },
  tab_comissao_periodo: { flag: 'comissao_periodo' },
  tab_fases: { flag: 'fases' },
  tab_motivo_inadimplencia: { flag: 'motivo_inadimplencia' },
  tab_tags_marcacoes: { flag: 'tags_marcacoes' },
  tab_categoria_cred: { flag: 'categoria_cred' },
  tab_segmento_cred: { flag: 'segmentos_cred' },
  tab_segmento_dev: { flag: 'segmentos_dev' },
  tab_especie_titulo: { flag: 'especie_titulo' },
  tabelas_discador: { flag: 'tabelas_discador' },
  tab_param_cob: { flag: 'param_cob' },
  tab_param_car: { flag: 'param_car' },
  tab_camp_desc: { flag: 'camp_desc' },
  tab_modal_contr: { flag: 'modal_contrato' },
  tab_comissao_escalonada: { flag: 'comissao_escalonada' },
  tab_modelos_pc: { flag: 'modelos_pc' },
  tab_inclusao_negativacao: {
    flag: 'inclusao_negativacao',
    planos: PLANOS_PRO,
  },
  tab_retirada_negativacao: {
    flag: 'retirada_negativacao',
    planos: PLANOS_PRO,
  },

  //* ***************************************************** FINANCEIRO */
  modulo_financeiro: {
    qualquerFlag: [
      'receb_bloq',
      'receb_v',
      'baixas_v',
      'prest_v',
      'bol_v',
      'che_v',
      'rec_v',
      'pag_v',
      'mov_v',
      'pix_v',
      'fat_v',
    ],
  },

  fin_crec_vis: { flag: 'rec_v' },
  fin_crec_cad: { flag: 'rec_c' },
  fin_crec_alt: { flag: 'rec_a' },
  fin_crec_del: { flag: 'rec_e' },
  fin_crec_quit: { flag: 'rec_q' },

  fin_cpag_vis: { flag: 'pag_v' },
  fin_cpag_cad: { flag: 'pag_c' },
  fin_cpag_alt: { flag: 'pag_a' },
  fin_cpag_del: { flag: 'pag_e' },
  fin_cpag_quit: { flag: 'pag_q' },

  fin_movc_vis: { flag: 'mov_v' },
  fin_movc_cad: { flag: 'mov_c' },
  fin_movc_alt: { flag: 'mov_a' },
  fin_movc_del: { flag: 'mov_e' },

  fin_rec_vis: { flag: 'receb_v' },
  fin_rec_cad: { flag: 'receb_c' },
  fin_rec_alt: { flag: 'receb_a' },
  fin_rec_del: { flag: 'receb_e' },
  fin_rec_baixar: { flag: 'receb_bai' },
  fin_rec_ident: { flag: 'receb_bloq' },
  fin_rec_recibo: { flag: 'receb_recibo' },
  fin_rec_bloq_ident: { sempre: true, negociador: true },

  fin_baixas_vis: { flag: 'baixas_v' },
  fin_baixas_alt_neg: { flag: 'alt_neg_baixa' },

  fin_pc_vis: { flag: 'prest_v' },
  fin_pc_cad: { flag: 'prest_c' },
  fin_pc_alt: { flag: 'prest_a' },
  fin_pc_del: { flag: 'prest_e' },
  fin_pc_est: { flag: 'prest_est' },
  fin_pc_audit: { flag: 'prest_conf' },
  fin_pc_imprimir: { flag: 'prest_i' },

  fin_rep_vis: { flag: 'rep_v' },
  fin_rep_repassar: { flag: 'rep_r' },
  fin_rep_baixar: { flag: 'rep_bai' },

  fin_cheq_vis: { flag: 'che_v', negociador: true },
  fin_cheq_cad: { flag: 'che_c' },
  fin_cheq_alt: { flag: 'che_a' },
  fin_cheq_del: { flag: 'che_e' },
  fin_cheq_est: { flag: 'che_est' },
  fin_cheq_comp: { flag: 'che_comp' },

  fin_bol_vis: { flag: 'bol_v', negociador: true },
  fin_bol_cad: { flag: 'bol_c' },
  fin_bol_alt: { flag: 'bol_a' },
  fin_bol_del: { flag: 'bol_e' },
  fin_bol_liq: { flag: 'bol_liq' },
  fin_bol_can: { flag: 'bol_can' },
  fin_bol_rem: { flag: 'bol_rem' },
  fin_bol_ret: { flag: 'bol_ret' },
  fin_bol_imp: { flag: 'bol_i', negociador: true },

  fin_pix_vis: { todasFlags: ['ativa_pix_cob', 'pix_v'] },
  fin_cartao_vis: { todasFlags: ['ativa_cartao_cob', 'pix_v'] },
  fin_fat_vis: { flag: 'fat_v' },
  fin_nf: { flag: 'fin_nf', planos: PLANOS_PRO },
  fin_alega_pag: { flag: 'alega_pag', planos: PLANOS_PRO },

  //* ***************************************************** BACKOFFICE */
  modulo_backoffice: {
    qualquerFlag: [
      'ctrl_desk',
      'hig_dev',
      'mtit',
      'camp_email',
      'camp_sms',
      'camp_bol',
      'acordos_lib',
      'devol',
      'hist_acessos',
    ],
  },

  back_baixas_titulos_planilha: { flag: 'baixa_titulo_planilha' },
  back_baixas_titulos_lote: { flag: 'baixa_titulo_lote' },
  back_migrar_processos: { flag: 'migrar_processos' },
  back_encerrar_processo_sem_saldo: { flag: 'encerra_proc_sem_saldo' },
  back_distribuicao_processo_planilha: { flag: 'distribuicao_planilha' },
  back_alterar_neg_acordo_planilha: { flag: 'alterar_neg_acordo' },
  back_atualizar_status_planilha: { flag: 'atualiza_status_proc_planilha' },
  back_devedor_titulo_excluido: { flag: 'devedor_titulo_excluido' },
  back_atualiza_fones: { flag: 'atualiza_fones' },
  back_higienizacao_dev: { flag: 'hig_dev' },
  back_importacoes_tit: { flag: 'imp_titulos' },
  back_importacao_oco: { flag: 'imp_oco' },
  back_manutencao_tit: { flag: 'mtit' },
  back_acoes_email: { flag: 'camp_email' },
  back_acoes_sms: { flag: 'camp_sms' },
  back_acoes_boletos: { flag: 'camp_bol' },
  back_liberacao_acordo: { flag: 'acordos_lib' },
  back_devolucao: { flag: 'devol' },
  back_distribuicao: { flag: 'distr_tit' },
  back_hist_acessos: { flag: 'hist_acessos' },
  back_documentos_digitais: { flag: 'doc_digital' },
  back_titulos_a_liberar: { flag: 'titulos_a_liberar' },
  back_controldesk: { flag: 'ctrl_desk' },
  back_negativacao: { flag: 'bol_conf', planos: PLANOS_PRO },
  back_fila_acionamento: {
    flag: 'fila_acionamento',
    planos: PLANOS_STARTUP_PRO,
  },
  back_discador: { flag: 'discador', planos: PLANOS_STARTUP_PRO },
  back_automacoes: { flag: 'back_automacoes', planos: PLANOS_BUSINESS },
  automacoes: { sempre: true, planos: PLANOS_STARTUP_PRO },
  bi: { sempre: true, planos: PLANOS_PRO },
  ...granular('back.ftp', 'back.higienizacao.tit'),

  //* ***************************************************** DIGITAL */
  modulo_digital: {
    qualquerAcesso: [
      'digital.conversas',
      'digital_agente_virtual',
      'digital_portal_neg',
      'digital_portal_credor',
      'digital_regua_cobranca',
    ],
  },

  ...granular('digital.conversas'),
  digital_portal_neg: { flag: 'portal_neg', planos: PLANOS_PRO },
  digital_agente_virtual: { flag: 'agente_virtual' },
  digital_portal_credor: { flag: 'back_portal_credor' },
  digital_regua_cobranca: { flag: 'back_regua_cobranca', planos: PLANOS_PRO },

  //* ***************************************************** DASHBOARDS */
  modulo_dashboards: {
    qualquerGranular: [
      'dash.metas.config',
      'dash.metas.empresa',
      'dash.metas.equipe',
      'dash.metas.individual',
    ],
  },
  ...granular(
    'dash.metas.config',
    'dash.metas.empresa',
    'dash.metas.equipe',
    'dash.metas.individual',
    'dash.gerencial.geral_carteiras',
    'dash.gerencial.gestao_acordos'
  ),

  //* ***************************************************** COMERCIAL */
  modulo_comercial: { flag: 'comercial' },

  //* ***************************************************** RELATORIOS */
  modulo_relatorios: {
    qualquerFlag: [
      'rel_cons_geral',
      'rel_hist_oco',
      'rel_tit',
      'rel_cre',
      'rel_dev',
      'rel_cons_com',
      'rel_fin',
      'rel_ger',
      'remret',
      'rel_doc',
    ],
  },

  rel_01: { flag: 'rel_cons_geral' },
  rel_02: { flag: 'rel_hist_oco', negociador: true },
  rel_03: { flag: 'rel_tit' },
  rel_04: { flag: 'rel_cre' },
  rel_05: { flag: 'rel_dev' },
  rel_06: { flag: 'rel_neg' },
  rel_07: { flag: 'rel_cons_com' },
  rel_08: { flag: 'rel_fin' },
  rel_09: { flag: 'rel_ger' },
  rel_remessa: { flag: 'remret' },
  rel_gerador: { flag: 'rel_doc' },
  rel_pdf: { flag: 'rel_pdf' },
  rel_personalizados: { flag: 'rel_personalizados', planos: PLANOS_PRO },

  //* ***************************************************** CONFIGURACOES */
  modulo_configuracoes: {
    qualquerFlag: [
      'config',
      'layout_imp',
      'layout_hig',
      'status_proc',
      'status_hist',
      'contas_email',
      'nivel_acesso',
      'ip_liberados',
      'horario_acesso',
      'politica_senha',
    ],
  },

  conf_gerais: { flag: 'config' },
  conf_layouts_imp: { flag: 'layout_imp' },
  conf_layouts_hig: { flag: 'layout_hig' },
  conf_status_proc: { flag: 'status_proc' },
  conf_status_hist: { flag: 'status_hist' },
  conf_ip_liberado: { flag: 'ip_liberados' },
  conf_horario_acesso: { flag: 'horario_acesso' },
  conf_politica_de_senhas: { flag: 'politica_senha' },
  conf_classifica_tit: { flag: 'status_hist' },
  conf_emails: { flag: 'contas_email' },
  conf_niveis: { flag: 'nivel_acesso' },

  //* ***************************************************** AVANCADO */
  avancado: {
    qualquerGranular: [
      'cob.processo.del_oco',
      'cob.processo.reabrir_titulo_quitado',
    ],
  },
  ...granular('cob.processo.del_oco', 'cob.processo.reabrir_titulo_quitado'),

  //* ***************************************************** DIVERSOS */
  acordo_reabrir: { flag: 'receb_bai' },
  acordo_rec_cad: { flag: 'receb_c' },

  // Semântica invertida: sinaliza que o recurso PRO deve aparecer como upsell
  funcionalidade_pro: { sempre: true, planos: PLANOS_STARTUP },
};

/**
 * `unificar_grupo` bloqueia perfis não-negociadores, exceto nos códigos abaixo.
 * No arquivo antigo a isenção era posicional: valia para tudo que estivesse
 * declarado acima da checagem de `unificar_grupo`.
 */
export const ISENTOS_UNIFICAR_GRUPO = new Set([
  'cob_exportar_xls',
  'cob_excluir_digitalizacao',
  'conf_neg_ativa_credor',
  'conf_neg_inativa_obs_credor',
  'acesso_taxa_contrato',
  'cob_boletos',
  'cob_pix',
  'cob_historicos',
  'cob_acordos_lib',
  'cob_recident',
  'instrucao_online',
  'mensagens.colab',
  'cob_modelo_oco',
  'cob_propostas',
  'fin_rec_bloq_ident',
  'cob_agenda_ret',
  'cob_acordos_prog',
  'cob_gravacoes_chamadas',
  'cob_comissao',
  'cob_acionar',
  'cob.processo.visualizar',
  'modulo_cadastros',
  'cad_colaborador_v',
  'cad_colaborador_c',
  'cad_colaborador_a',
  'cad_colaborador_e',
  'cad_colaborador_grupo',
  'cad_devedor_v',
  'fin_bol_vis',
  'fin_bol_imp',
  'rel_02',
]);
